/*
 * Copyright '2025' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { UserContext } from "sirius-platform-support-library/shared/authentication/user-context/user-context";
import { Permission } from "sirius-platform-support-library/shared/authorization/models/permission";
import {
  IPermissionsProvider,
  PermissionsProviderRefreshRequestedCallback,
} from "sirius-platform-support-library/shared/authorization/providers/permissions-provider.interface";
import { ObjectUtility } from "sirius-platform-support-library/utilities/object-utility";
import { IAuthenticationService } from "sirius-platform-support-library/shared/authentication/authentication-service.interface";
import { environment } from "../../../environments/environments";

export const UserClaimsPermissionProviderTypeName =
  "UserClaimsPermissionProvider";

export class UserClaimsPermissionProvider implements IPermissionsProvider {
  public static readonly NAMESPACE = "tdp";
  public static readonly USER_STATUS_ACTIVE_PERMISSION = "userStatusActive";
  public static readonly VALID_EMAIL_DOMAIN_PERMISSION = "validEmailDomain";
  public static readonly AUTHENTICATED_AS_IMPERSONATOR_PERMISSION =
    "authenticatedAsImpersonator";
  public static readonly COMPANY_ATTESTED_PERMISSION = "companyAttested";

  private refreshRequestedCallback?: PermissionsProviderRefreshRequestedCallback;
  private appliedPermissions: Permission[] = [];
  private authenticationService: IAuthenticationService;

  public constructor(authenticationService: IAuthenticationService) {
    ObjectUtility.assignOnObjectPath<UserClaimsPermissionProvider>(
      "window.sirius.permissions",
      this
    );
    this.authenticationService = authenticationService;
    this.applyPermission().catch((e) => console.error(e));
  }

  public getNamespace(): string {
    return UserClaimsPermissionProvider.NAMESPACE;
  }

  public async getPermissions(userContext: UserContext): Promise<Permission[]> {
    if (this.appliedPermissions.length === 0) {
      await this.applyPermission();
    }
    return this.appliedPermissions;
  }

  public async refresh?(): Promise<void> {
    try {
      await this.applyPermission();
      if (this.refreshRequestedCallback) {
        this.refreshRequestedCallback(this.getNamespace());
      }
    } catch (e) {
      console.error(e);
    }
  }

  public onRefreshRequested?(
    callback: PermissionsProviderRefreshRequestedCallback
  ): void {
    this.refreshRequestedCallback = callback;
  }

  private async applyPermission() {
    this.appliedPermissions = [];
    const userClaims =
      this.authenticationService.getUserContext()?.claims ?? {};

    if ((userClaims["userStatus"] ?? "").toString() == "ACTIVE") {
      const userStatusPermission: Permission = {
        namespace: UserClaimsPermissionProvider.NAMESPACE,
        code: UserClaimsPermissionProvider.USER_STATUS_ACTIVE_PERMISSION,
      };

      if (
        !this.appliedPermissions.some(
          (p) => p.code === userStatusPermission.code
        )
      ) {
        this.appliedPermissions.push(userStatusPermission);
      }
    }

    if ((userClaims["isEmailDomainAllowed"] ?? true).toString() === "true") {
      const validEmailDomainPermission: Permission = {
        namespace: UserClaimsPermissionProvider.NAMESPACE,
        code: UserClaimsPermissionProvider.VALID_EMAIL_DOMAIN_PERMISSION,
      };

      if (
        !this.appliedPermissions.some(
          (p) => p.code === validEmailDomainPermission.code
        )
      ) {
        this.appliedPermissions.push(validEmailDomainPermission);
      }
    }

    if (
      (userClaims["authenticatedAsImpersonator"] ?? false).toString() === "true"
    ) {
      const impersonatorPermission: Permission = {
        namespace: UserClaimsPermissionProvider.NAMESPACE,
        code: UserClaimsPermissionProvider.AUTHENTICATED_AS_IMPERSONATOR_PERMISSION,
      };

      if (
        !this.appliedPermissions.some(
          (p) => p.code === impersonatorPermission.code
        )
      ) {
        this.appliedPermissions.push(impersonatorPermission);
      }
    }

    if (
      !environment.companyAttestationEnabled ||
      (userClaims["isAttestationExpired"] ?? false).toString() === "false"
    ) {
      const companyAttestedPermission: Permission = {
        namespace: UserClaimsPermissionProvider.NAMESPACE,
        code: UserClaimsPermissionProvider.COMPANY_ATTESTED_PERMISSION,
      };

      if (
        !this.appliedPermissions.some(
          (p) => p.code === companyAttestedPermission.code
        )
      ) {
        this.appliedPermissions.push(companyAttestedPermission);
      }
    }
  }
}
